import moment from "moment";
import { useState, useEffect } from "react";
import { AppBox, AppPaper, AppSkeletonMatchCardList, AppText, AppTitle } from "../../../../commons/components";
import { MatchStatusEnum } from "../../../../commons/enums/match-status-enum";
import { SortOrderEnum } from "../../../../commons/enums/sort-order-enum";
import { StatusEnum } from "../../../../commons/enums/status-enum";
import { FootballMatchDetail } from "../../../../commons/models/football/football-match-detail";
import { groupArrayByProperty } from "../../../../commons/utilities/array-utils";
import { formatDateTime } from "../../../../commons/utilities/date-utils";
import { useAppDispatch, useAppSelector } from "../../../../hooks/app";
import {
  cleanUpMatches,
  fetchMatches,
  IFootballMatchesOption,
} from "../../../../redux/slices/football/admin/dashboard/manage-football-match-list-slice";
import ManageMatchCard from "../../teams/components/ManageMatchCard";

export default function MatchList() {
  const dispatch = useAppDispatch();
  const { matches, matchesStatus } = useAppSelector((state) => state.footballManageMatches);
  const [groupedUpcomingMatches, setGroupedUpcomingMatches] = useState<
    { [x: string]: string | FootballMatchDetail[]; items: FootballMatchDetail[] }[]
  >([]);
  const [groupedLiveMatches, setGroupedLiveMatches] = useState<
    { [x: string]: string | FootballMatchDetail[]; items: FootballMatchDetail[] }[]
  >([]);
  const [groupedFinishedMatches, setGroupedFinishedMatches] = useState<
    { [x: string]: string | FootballMatchDetail[]; items: FootballMatchDetail[] }[]
  >([]);

  useEffect(() => {
    return () => {
      dispatch(cleanUpMatches());
    };
  }, []);

  useEffect(() => {
    dispatch(cleanUpMatches());
    let matchesOption: IFootballMatchesOption = {
      sort: "scheduledAt",
      sortOrder: SortOrderEnum.Ascending,
      beforeDate: formatDateTime(moment().subtract(1, "weeks")),
      afterDate: formatDateTime(moment().add(1, "weeks")),
      page: 0,
    };
    dispatch(fetchMatches(matchesOption));
  }, []);

  useEffect(() => {
    if (matches.all().length > 0) {
      const matchList = matches.all();
      const upcomingMatches = matchList.filter((match) => match.status === MatchStatusEnum.Upcoming);
      const liveMatches = matchList.filter((match) => match.status === MatchStatusEnum.Live);
      const finishedMatches = matchList
        .filter((match) => match.status === MatchStatusEnum.Finished)
        .sort((a, b) => moment(b.scheduledAt).diff(moment(a.scheduledAt)));
      const formattedLiveMatches: FootballMatchDetail[] = liveMatches.map((match) => {
        const formattedMatch = new FootballMatchDetail({
          ...match,
          competitionName: match.competition?.title,
        });
        return formattedMatch;
      });
      const formattedUpcomingMatches: FootballMatchDetail[] = upcomingMatches.map((match) => {
        const formattedMatch = new FootballMatchDetail({
          ...match,
          competitionName: match.competition?.title,
        });
        return formattedMatch;
      });
      const formattedFinishedMatches: FootballMatchDetail[] = finishedMatches.map((match) => {
        const formattedMatch = new FootballMatchDetail({
          ...match,
          competition: match.competition,
          competitionName: match.competition?.title,
        });
        return formattedMatch;
      });
      setGroupedLiveMatches(groupArrayByProperty(formattedLiveMatches, "competitionName"));
      setGroupedUpcomingMatches(groupArrayByProperty(formattedUpcomingMatches, "competitionName"));
      setGroupedFinishedMatches(groupArrayByProperty(formattedFinishedMatches, "competitionName"));
    } else {
      cleanGroupingArray();
    }
  }, [matches]);

  /**
   * TODO: Implement socket to match fetching.
   */

  // useEffect(() => {
  //   let intervalId: NodeJS.Timeout;
  //   intervalId = setInterval(() => {
  //     dispatch(cleanUpMatches());
  //     let matchesOption: IFootballMatchesOption = {
  //       sort: "scheduledAt",
  //       sortOrder: SortOrderEnum.Ascending,
  //       beforeDate: formatDateTime(moment().subtract(3, "weeks")),
  //       afterDate: formatDateTime(moment().add(3, "weeks")),
  //       page: 0,
  //     };
  //     dispatch(fetchMatches(matchesOption));
  //   }, 30000);
  //   return () => {
  //     if (intervalId) {
  //       clearInterval(intervalId);
  //     }
  //   };
  // }, [matches]);

  const cleanGroupingArray = () => {
    setGroupedFinishedMatches([]);
    setGroupedLiveMatches([]);
    setGroupedUpcomingMatches([]);
  };

  return (
    <AppBox flexDirection="column" gap="xs" mb="md" className="w-100">
      <AppTitle as="h5">Matches</AppTitle>
      {matchesStatus === StatusEnum.Loading ? (
        <>
          <AppSkeletonMatchCardList />
          <AppSkeletonMatchCardList />
          <AppSkeletonMatchCardList />
        </>
      ) : (
        <>
          <>
            {matches.all().length > 0 ? (
              <>
                {groupedLiveMatches.length > 0 && (
                  <AppPaper shadow="xs" padding="xs">
                    <AppBox flexDirection="column" gap="xs">
                      <AppTitle as="h6">Live</AppTitle>
                      {groupedLiveMatches.map((groupedMatch, index) => (
                        <AppBox flexDirection="column" gap="2xs" key={index}>
                          <AppText textAlign="center">{groupedMatch.competitionName as string}</AppText>
                          <AppBox flexDirection="column" gap="xs">
                            {groupedMatch?.items.map((match, index) => (
                              <ManageMatchCard key={index} match={match} withBorder />
                            ))}
                          </AppBox>
                        </AppBox>
                      ))}
                    </AppBox>
                  </AppPaper>
                )}
                {groupedUpcomingMatches.length > 0 && (
                  <AppPaper shadow="xs" padding="xs">
                    <AppBox flexDirection="column" gap="xs">
                      <AppTitle as="h6">Upcoming</AppTitle>
                      {groupedUpcomingMatches.map((groupedMatch, index) => (
                        <AppBox flexDirection="column" gap="2xs" key={index}>
                          <AppText textAlign="center">{groupedMatch.competitionName as string}</AppText>
                          <AppBox flexDirection="column" gap="xs">
                            {groupedMatch?.items.map((match, index) => (
                              <ManageMatchCard key={index} match={match} withBorder />
                            ))}
                          </AppBox>
                        </AppBox>
                      ))}
                    </AppBox>
                  </AppPaper>
                )}
                {groupedFinishedMatches.length > 0 && (
                  <AppPaper shadow="xs" padding="xs">
                    <AppBox flexDirection="column" gap="xs">
                      <AppTitle as="h6">Recent</AppTitle>
                      {groupedFinishedMatches.map((groupedMatch, index) => (
                        <AppBox flexDirection="column" gap="2xs" key={index}>
                          <AppText textAlign="center">{groupedMatch.competitionName as string}</AppText>
                          <AppBox flexDirection="column" gap="xs">
                            {groupedMatch?.items.map((match, index) => (
                              <ManageMatchCard key={index} match={match} withBorder />
                            ))}
                          </AppBox>
                        </AppBox>
                      ))}
                    </AppBox>
                  </AppPaper>
                )}
              </>
            ) : (
              <AppPaper shadow="xs" className="w-100">
                <AppBox
                  flexDirection="column"
                  gap="xs"
                  alignItems="center"
                  justifyContent="center"
                  className="w-100"
                  style={{ height: "20rem" }}
                >
                  <AppTitle as="h6">No match to be found</AppTitle>
                  <AppText color="muted">There are not any recent scheduled / completed matches.</AppText>
                </AppBox>
              </AppPaper>
            )}
          </>
        </>
      )}
    </AppBox>
  );
}
